import React, { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import {
  BrandCreatorCard,
  getBrandCreatorDirectory,
} from '../../api/payment/brand-deal.api';

/** Creator đã chọn để mời kèm giá, gửi cùng lúc tạo chiến dịch riêng tư. */
export interface InvitePick {
  creatorId: string;
  displayName: string;
  priceVnd: number;
}

/** Creator chưa đặt giá đăng bài thì đề xuất mức này, người đặt sửa lại được. */
export const DEFAULT_INVITE_PRICE_VND = 300000;

export const defaultInvitePrice = (c: BrandCreatorCard): number =>
  c.postPriceVnd && c.postPriceVnd > 0
    ? c.postPriceVnd
    : DEFAULT_INVITE_PRICE_VND;

const Avatar: React.FC<{ c: BrandCreatorCard }> = ({ c }) => (
  <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-sm font-semibold dark:bg-slate-600">
    {c.avatar ? (
      <img src={c.avatar} alt="" className="size-full object-cover" />
    ) : (
      c.displayName.trim()[0]?.toUpperCase() || '?'
    )}
  </span>
);

/**
 * Chọn creator để mời ngay lúc tạo chiến dịch riêng tư: tìm trong danh bạ creator, bấm "Chọn" thì thêm vào
 * danh sách với giá đề xuất (giá đăng bài của creator nếu có), sửa giá hoặc bỏ từng người.
 */
const CampaignInvitePicker: React.FC<{
  adAccountId?: string;
  value: InvitePick[];
  onChange: (v: InvitePick[]) => void;
}> = ({ adAccountId, value, onChange }) => {
  const [q, setQ] = useState('');
  const [creators, setCreators] = useState<BrandCreatorCard[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      getBrandCreatorDirectory({
        q: q.trim() || undefined,
        adAccountId: adAccountId || undefined,
      })
        .then((r) => alive && setCreators(r.items))
        .catch(() => alive && setCreators([]))
        .finally(() => alive && setLoading(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, adAccountId]);

  const picked = new Set(value.map((v) => v.creatorId));

  const add = (c: BrandCreatorCard) => {
    if (picked.has(c.userId)) return;
    onChange([
      ...value,
      {
        creatorId: c.userId,
        displayName: c.displayName,
        priceVnd: defaultInvitePrice(c),
      },
    ]);
  };

  const setPrice = (creatorId: string, priceVnd: number) =>
    onChange(
      value.map((v) => (v.creatorId === creatorId ? { ...v, priceVnd } : v))
    );

  const remove = (creatorId: string) =>
    onChange(value.filter((v) => v.creatorId !== creatorId));

  return (
    <div className="space-y-3">
      {value.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold ui-text">
            Đã chọn {value.length} creator
          </div>
          {value.map((v) => (
            <div
              key={v.creatorId}
              className="flex flex-wrap items-center gap-2 rounded-lg border ui-divider p-2"
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium ui-text">
                {v.displayName}
              </span>
              <input
                type="number"
                min={0}
                aria-label={`Giá cho ${v.displayName}`}
                value={v.priceVnd}
                onChange={(e) => setPrice(v.creatorId, Number(e.target.value))}
                className="w-36 rounded-md border ui-divider bg-transparent px-2 py-1 text-sm outline-none ui-text"
              />
              <span className="text-xs ui-muted">đ</span>
              <button
                type="button"
                onClick={() => remove(v.creatorId)}
                className="rounded-md border ui-divider px-2 py-1 text-xs font-semibold ui-text hover:bg-black/5 dark:hover:bg-white/5"
              >
                Bỏ
              </button>
            </div>
          ))}
        </div>
      )}

      <label className="flex items-center gap-2 rounded-lg border ui-divider px-3 py-2">
        <Search size={16} className="ui-muted" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tìm theo tên hoặc username"
          className="w-full bg-transparent text-sm outline-none ui-text"
        />
      </label>

      {loading && (
        <div className="flex items-center gap-2 text-sm ui-muted">
          <Loader2 size={14} className="animate-spin" /> Đang tải…
        </div>
      )}
      {!loading && creators?.length === 0 && (
        <p className="text-sm ui-muted">Không tìm thấy creator nào.</p>
      )}
      {!loading && creators && creators.length > 0 && (
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {creators.map((c) => {
            const isPicked = picked.has(c.userId);
            return (
              <div
                key={c.userId}
                className="flex items-center gap-3 rounded-xl border ui-divider p-3"
              >
                <Avatar c={c} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium ui-text">
                    {c.displayName}
                  </span>
                  <span className="block truncate text-xs ui-muted">
                    @{c.userName} · {c.followers.toLocaleString('vi-VN')} người
                    theo dõi
                    {c.postPriceVnd
                      ? ` · Giá đăng bài ${c.postPriceVnd.toLocaleString(
                          'vi-VN'
                        )}đ`
                      : ''}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={isPicked}
                  onClick={() => add(c)}
                  className="shrink-0 rounded-lg bg-[#0057FF] px-3 py-1.5 text-xs font-semibold text-white disabled:bg-transparent disabled:text-[#0057FF] dark:disabled:text-blue-300"
                >
                  {isPicked ? 'Đã chọn' : 'Chọn'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampaignInvitePicker;
